import React, { useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as supplierService from "../../services/supplierService";
import { DataTable, ProcBadge, act, fileSize, linkBtn, openSigned } from "../Proc/procUi";
import { DetailGrid, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, Tabs, cardStyle, formatDate, inputStyle, pageStyle, useLoad } from "../../components/ui";

// The supplier's own company: details, the documents its buyers ask for, and its people.
// Only the main user edits the details or the people; everyone can upload documents.

function Details({ canEdit }) {
  const { data, loading, error, reload } = useLoad(() => supplierService.getCompany(), []);
  const [editing, setEditing] = useState(false);
  const c = data || {};
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div style={{ ...cardStyle, padding: 20, maxWidth: 720 }}>
        <div className="d-flex justify-content-between mb-3">
          <h6 className="mb-0">Company details</h6>
          {canEdit ? (
            <button type="button" style={linkBtn} onClick={() => setEditing(true)}>
              Edit
            </button>
          ) : null}
        </div>
        <DetailGrid
          items={[
            ["Company name", c.name],
            ["Registration number", c.registration_no],
            ["Tax ID (TRN / GST)", c.tax_id],
            ["Country", c.country],
            ["Address", c.address],
            ["Email", c.email],
            ["Mobile", c.phone],
          ]}
        />
        {!canEdit ? <div style={{ fontSize: 12, color: "#6B7280", marginTop: 12 }}>Only your company's main user can change these details.</div> : null}
      </div>
      <FormDrawer
        show={editing}
        title="Company details"
        initial={{ ...c }}
        onHide={() => setEditing(false)}
        onSave={(clean) => supplierService.saveCompany(clean)}
        onDone={reload}
        fields={[
          { key: "name", label: "Company name", required: true, max: 200 },
          { key: "registration_no", label: "Registration number", max: 64 },
          { key: "tax_id", label: "Tax ID (TRN / GST)", max: 64 },
          { key: "country", label: "Country", max: 100 },
          { key: "address", label: "Address", type: "textarea", max: 500 },
          { key: "email", label: "Company email", type: "email" },
          { key: "phone", label: "Company mobile", type: "mobile" },
        ]}
      />
    </LoadState>
  );
}

function UploadPanel({ type, onHide, onDone }) {
  const [expiry, setExpiry] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const input = useRef(null);
  const reset = () => {
    setExpiry("");
    setFile(null);
    if (input.current) input.current.value = "";
  };
  const close = () => {
    reset();
    onHide();
  };
  const save = async () => {
    if (!file) return toast.error("Choose the file to upload");
    setSaving(true);
    try {
      const res = await supplierService.uploadDocument({ document_type_code: type.code, expiry_date: expiry }, file);
      if (res?.success) {
        toast.success(res.message || "Document uploaded");
        onDone();
        close();
      } else toast.error(res?.message || "The document could not be uploaded");
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={!!type}
      onHide={() => !saving && close()}
      title={type ? `Upload ${type.name}` : "Upload"}
      width={460}
      footer={
        <>
          <SecondaryButton onClick={close} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving}>
            Upload
          </PrimaryButton>
        </>
      }
    >
      {type ? (
        <>
          <Field label="File" required htmlFor="sup-doc-file" hint="PDF, JPG, PNG or Word, up to 10 MB. A new upload replaces the current one.">
            <input id="sup-doc-file" ref={input} type="file" className="form-control" accept=".pdf,.jpg,.jpeg,.png,.docx" onChange={(e) => setFile(e.target.files[0] || null)} style={inputStyle} />
          </Field>
          {type.has_expiry ? (
            <Field label="Expiry date" required htmlFor="sup-doc-expiry">
              <input id="sup-doc-expiry" type="date" className="form-control" value={expiry} onChange={(e) => setExpiry(e.target.value)} style={inputStyle} />
            </Field>
          ) : null}
        </>
      ) : null}
    </SlidePanel>
  );
}

function Documents() {
  const { data, loading, error, reload } = useLoad(() => supplierService.getDocuments(), []);
  const [uploading, setUploading] = useState(null);
  const types = (data && data.types) || [];
  const docs = (data && data.documents) || [];
  const rows = types.map((t) => ({ type: t, doc: docs.find((d) => d.document_type_code === t.code) || null }));
  const state = (r) => (!r.doc ? <ProcBadge status="PENDING" label="Not uploaded" /> : r.doc.expired ? <ProcBadge status="EXPIRED" /> : <ProcBadge status="COMPLETE" label="Uploaded" />);

  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>Upload each document once: every buyer that asks for it sees the same copy and reviews it for themselves.</div>
      <DataTable
        minWidth={720}
        rowKey={(r) => r.type.code}
        rows={rows}
        empty={<EmptyState title="No documents requested" text="Your buyers have not asked for any company documents yet." />}
        columns={[
          { label: "Document", render: (r) => (<><strong>{r.type.name}</strong>{r.type.is_mandatory ? <span style={{ color: "#991B1B", fontSize: 12 }}> · required</span> : null}</>) },
          { label: "Status", render: state },
          { label: "File", render: (r) => (r.doc ? `${r.doc.file_name} · ${fileSize(r.doc.size)}` : "—") },
          { label: "Expires", render: (r) => (r.doc && r.doc.expiry_date ? formatDate(r.doc.expiry_date) : "—") },
          {
            label: "Buyers' review",
            render: (r) =>
              r.doc && r.doc.reviews.length ? (
                <div style={{ fontSize: 12 }}>
                  {r.doc.reviews.map((v) => (
                    <div key={v.buyer_id}>
                      {v.buyer_name}: <ProcBadge status={v.status} />
                      {v.status === "REJECTED" && v.note ? <span style={{ color: "#991B1B" }}> {v.note}</span> : null}
                    </div>
                  ))}
                </div>
              ) : (
                "—"
              ),
          },
          {
            label: "",
            render: (r) => (
              <span className="d-inline-flex gap-2" style={{ whiteSpace: "nowrap" }}>
                <button type="button" style={linkBtn} onClick={() => setUploading(r.type)}>
                  {r.doc ? "Replace" : "Upload"}
                </button>
                {r.doc ? (
                  <button type="button" style={linkBtn} onClick={() => openSigned(() => supplierService.getDocumentUrl(r.doc.id), toast)}>
                    View
                  </button>
                ) : null}
              </span>
            ),
          },
        ]}
      />
      <UploadPanel type={uploading} onHide={() => setUploading(null)} onDone={reload} />
    </LoadState>
  );
}

function People({ canEdit }) {
  const { data, loading, error, reload } = useLoad(() => supplierService.getUsers(), []);
  const [adding, setAdding] = useState(false);
  const toggle = (u) => act(() => supplierService.setUserStatus(u.user_id, u.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"), "Saved").then((r) => r && reload());
  return (
    <>
      {canEdit ? (
        <div className="mb-3">
          <PrimaryButton onClick={() => setAdding(true)}>Add a colleague</PrimaryButton>
        </div>
      ) : null}
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          minWidth={600}
          rowKey={(r) => r.user_id}
          rows={data || []}
          columns={[
            { label: "Name", render: (r) => `${r.first_name}${r.is_me ? " (you)" : ""}` },
            { label: "Role", render: (r) => (r.is_primary_user ? "Main user" : "Colleague") },
            { label: "Email", render: (r) => r.email },
            { label: "Mobile", render: (r) => r.phone_number },
            { label: "Status", render: (r) => <ProcBadge status={r.status} /> },
            {
              label: "",
              render: (r) =>
                canEdit && !r.is_me && !r.is_primary_user ? (
                  <button type="button" style={linkBtn} onClick={() => toggle(r)}>
                    {r.status === "ACTIVE" ? "Switch off" : "Switch on"}
                  </button>
                ) : null,
            },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={adding}
        title="Add a colleague"
        submitLabel="Add"
        initial={{}}
        onHide={() => setAdding(false)}
        onSave={(clean) => supplierService.addUser(clean)}
        onDone={reload}
        fields={[
          { key: "first_name", label: "Name", required: true, max: 100 },
          { key: "email", label: "Email", type: "email", required: true, hint: "They sign in with this email or their mobile number: a one-time code is sent, no password" },
          { key: "phone", label: "Mobile", type: "mobile", required: true },
        ]}
      />
    </>
  );
}

export default function SupplierCompany() {
  const { me } = useOutletContext() || {};
  const [params, setParams] = useSearchParams();
  const tab = ["details", "documents", "people"].includes(params.get("tab")) ? params.get("tab") : "details";
  const main = !!(me && me.is_primary_user);
  const TABS = [
    { key: "details", label: "Details", render: () => <Details canEdit={main} /> },
    { key: "documents", label: "Documents", render: () => <Documents /> },
    { key: "people", label: "People", render: () => <People canEdit={main} /> },
  ];
  return (
    <div style={pageStyle}>
      <PageHeader title="Company" subtitle={me && me.company ? me.company.name : "Your company, documents and people"} />
      <Tabs tabs={TABS.map(({ key, label }) => ({ key, label }))} active={tab} onChange={(key) => setParams({ tab: key }, { replace: true })} />
      {TABS.find((t) => t.key === tab).render()}
    </div>
  );
}
